/**
 * UI CHANGE REQUESTS 3/4/5 - TELL THE ADMIN *WHY*, NOT JUST *THAT*.
 *
 * The backend classifier works out exactly why a request needs a human - Medicare Advantage, an
 * unconfirmed Part B, an active hospice election, a payer error - and persists it to
 * ivr.reviewReasons specifically so "an administrator is told WHY a request needs them, not merely
 * THAT it does" (ivr-request.schema.ts). Until now NO CLIENT READ IT. The admin saw "Needs Review"
 * and had to guess.
 *
 * Keep this map in sync with REVIEW_REASON in the backend ivr-classifier.ts.
 *
 * THE FALLBACK IS THE WHOLE POINT, AND IT IS THE LESSON OF THIS ENTIRE PLAN.
 * Every status bug on this feature had the same shape: A MISSING MAP KEY DID NOT CRASH - IT RENDERED
 * THE WRONG THING. A needs_review IVR showed an amber "Submitted" chip; the dashboard counted a status
 * that did not exist. So an unrecognized reason code here does NOT fall back to some other reason's
 * label, and it does NOT silently disappear. It degrades to a humanized form of the RAW CODE - which is
 * uglier, and TRUE. If the backend adds a 15th reason and nobody updates this file, the admin sees
 * "Some New Reason" instead of nothing at all, or worse, the wrong reason.
 */

export const REVIEW_REASON_LABELS = {
  service_dates_unverified:
    'The payer response does not confirm coverage for the requested service dates.',
  unsupported_primary_coverage:
    'This plan does not meet MedEffects\u2019 requirement for Original Medicare Part B as the primary payer.',
  // Billing-critical: these decide whether we get paid.
  part_b_not_confirmed: 'Medicare Part B not confirmed',
  medicare_advantage_plan: 'Medicare Advantage plan reported',
  medicare_not_primary_payer: 'Medicare is not the primary payer',
  active_hospice_election: 'Active hospice election - DME may be the hospice’s responsibility',
  // Reworded 9/7/26 (audit): says what to DO. It is a billing rule, not a coverage problem -
  // Medicare still pays as primary; Medicaid picks up the cost-sharing and the patient is never
  // balance-billed. About 4 in 10 verified IVRs carry it, and that is real data, not a bug.
  dual_eligible_qmb:
    'QMB (Medicare + Medicaid): bill Medicare as usual. Do not bill the patient for coinsurance or the deductible.',
  workers_comp_or_liability_payer: 'Workers’ comp or liability payer on file',

  // Plan shape.
  hmo_plan: 'HMO plan',
  back_office_payer_not_realtime: 'Payer is not real-time - verify manually',

  // The payer could not give us a usable answer.
  payer_returned_error: 'Payer returned an error',
  payer_invalid_subscriber_id: 'The payer requested a corrected Medicare ID',
  payer_invalid_subscriber_name: 'The payer requested a corrected patient name',
  verification_result_unconfirmed:
    'The verification result could not be confirmed. Do not submit another request; MedEffects must review it.',
  not_processed_by_payer: 'Payer did not process the request',
  no_coverage_status_returned: 'Payer returned no coverage status',
  unknown_coverage_status: 'Payer returned an unrecognized coverage status',
  demographic_mismatch: 'Patient demographics did not match the payer’s record',

  // Ours, not the payer's.
  missing_patient_identifiers: 'Missing or invalid patient information',
};

/**
 * Turn a reason code into something an admin can read.
 * An UNKNOWN code degrades to the humanized code, never to another reason's label and never to nothing.
 */
export function reviewReasonLabel(code) {
  if (!code || typeof code !== 'string') return null;
  if (REVIEW_REASON_LABELS[code]) return REVIEW_REASON_LABELS[code];

  return code
    .split('_')
    .filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

/**
 * Reasons that are NOTES, not warnings (mirrors ADVISORY_REASONS' billing half in the backend's
 * ivr-classifier.ts). A QMB flag never held up a decision since 8/25/26, and rendering it in the
 * same violet as "the payer returned an error" made a normal, common billing rule look like a
 * problem on most of the list. Blue and calm, then; the words carry the rule.
 */
export const INFORMATIONAL_REASONS = new Set(['dual_eligible_qmb']);

export function isInformationalReason(code) {
  return INFORMATIONAL_REASONS.has(code);
}

/** Codes to render: [{ code, label, informational }], dropping only genuinely empty entries. */
export function reviewReasonNotes(reasons) {
  if (!Array.isArray(reasons)) return [];
  return reasons
    .map((code) => ({
      code,
      label: reviewReasonLabel(code),
      informational: isInformationalReason(code),
    }))
    .filter((note) => Boolean(note.label));
}

/** Map a raw reviewReasons array to display labels, dropping only genuinely empty entries. */
export function reviewReasonLabels(reasons) {
  if (!Array.isArray(reasons)) return [];
  return reasons.map(reviewReasonLabel).filter(Boolean);
}

/** A manual rejection is not evidence that the patient's identifiers were wrong. */
export function ivrRejectionGuidance(record) {
  const reasons = Array.isArray(record?.reviewReasons) ? record.reviewReasons : [];
  const invalidId = reasons.includes('payer_invalid_subscriber_id');
  const invalidName = reasons.includes('payer_invalid_subscriber_name');
  if (invalidId && !invalidName) {
    return 'The payer could not verify the Medicare ID. Check it against the Medicare card, correct it, and submit a new IVR. This is not a coverage denial.';
  }
  if (invalidName && !invalidId) {
    return 'The payer could not verify the patient name. Check it against the Medicare card, correct it, and submit a new IVR. This is not a coverage denial.';
  }
  if (
    invalidId ||
    invalidName ||
    (![
      'payer_returned_error',
      'not_processed_by_payer',
      'back_office_payer_not_realtime',
      'service_dates_unverified',
      'verification_result_unconfirmed',
    ].some((code) => reasons.includes(code)) &&
      (reasons.includes('demographic_mismatch') || reasons.includes('missing_patient_identifiers')))
  ) {
    return 'Patient information could not be verified. Check the name and Medicare ID against the card, then submit a corrected IVR. This is not a coverage denial.';
  }
  return 'MedEffects rejected this request. Review the note from MedEffects for the reason and next steps. This is not a coverage denial.';
}

/** Only the closed API follow-up projection can describe automatic rechecks. */
export function ivrFollowUpNote(followUp, locale = 'en-US', outcome) {
  if (!followUp || typeof followUp !== 'object' || Array.isArray(followUp)) return null;
  const messages = {
    exhausted: 'Automatic rechecks have finished. MedEffects needs to review this result.',
    unavailable: 'This result needs MedEffects review. No automatic recheck is scheduled.',
    reconciliation:
      'This verification needs MedEffects review. Do not submit another IVR for this request.',
    completed:
      outcome === 'needs_review'
        ? 'Automatic follow-up finished. MedEffects needs to review this result.'
        : 'Automatic follow-up is complete.',
  };
  if (followUp.state === 'scheduled') {
    const value = followUp.nextCheckAt;
    if (
      typeof value !== 'string' ||
      !/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(value) ||
      !Number.isFinite(Date.parse(value)) ||
      new Date(value).toISOString() !== value
    )
      return null;
    if (Date.parse(value) <= Date.now()) {
      return { message: 'An automatic recheck is queued. You do not need to submit again.' };
    }
    return {
      message: 'Automatic recheck scheduled for {date}. You do not need to submit again.',
      values: { date: new Date(value).toLocaleString(locale) },
    };
  }
  return Object.hasOwn(messages, followUp.state) ? { message: messages[followUp.state] } : null;
}
